import { useState, useEffect } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import useHttp from "../../hooks/use-http";
import { endPoints } from "../../config/end-points";
import { getSubdomainUrl } from "../../config";
import { access_specifier } from "../../config/app.config";
import {
  FiArrowLeft, FiLayers, FiGlobe, FiFileText, FiInbox, FiCpu,
  FiUsers, FiCalendar, FiClock, FiUser, FiDollarSign, FiActivity,
  FiSmartphone, FiSend, FiPlus, FiKey, FiCheck, FiX, FiMail
} from "react-icons/fi";
import "./UserStats.css";

const formatNumber = (num) => {
  if (!num) return "0";
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric"
  });
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const getPlanLabel = (access) => {
  if (!access) return "Free";
  const codes = access.split(",").map(a => a.trim());
  for (const code of ["5", "9", "62", "61", "6", "4", "3", "2", "1"]) {
    if (codes.includes(code) && access_specifier[code]) return access_specifier[code];
  }
  return "Free";
};

const STATUS_COLORS = {
  published: { bg: "#DCFCE7", text: "#166534" },
  wireframe: { bg: "#F5F3FF", text: "#7C3AED" },
  draft: { bg: "#F3F4F6", text: "#6B7280" },
};

const UserStats = () => {
  const { user_id } = useParams();
  const { sendRequest } = useHttp();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sendRequest(
      { url: `${endPoints.adminUserStats}/${user_id}`, method: "GET" },
      (response) => {
        if (response.status === 200) {
          setData(response);
          document.title = `${endPoints.title} | ${response.user.first_name} ${response.user.last_name}`;
        }
        setLoading(false);
      }
    );
  }, [user_id, sendRequest]);

  if (loading) {
    return (
      <section className="mid_wrap ust-page">
        <div className="container">
          <div className="ust-loading">Loading user stats...</div>
        </div>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="mid_wrap ust-page">
        <div className="container">
          <div className="ust-loading">User not found.</div>
        </div>
      </section>
    );
  }

  const { user, projects, submissions, subUsers, recentActivity, aiKeys, smtp, whitelabel, autoresponderStatus } = data;

  const statCards = [
    { label: "Projects", value: user.projectCount, icon: FiLayers, color: "#8B5CF6" },
    { label: "Published", value: user.publishedCount, icon: FiGlobe, color: "#10B981" },
    { label: "Pages", value: user.totalPages, icon: FiFileText, color: "#3B82F6" },
    { label: "Submissions", value: user.submissionCount, icon: FiInbox, color: "#F59E0B" },
    { label: "AI Tokens", value: user.totalAiTokens, icon: FiCpu, color: "#EC4899" },
    { label: "Total Cost", value: `$${(user.totalCost || 0).toFixed(2)}`, icon: FiDollarSign, color: "#EF4444", raw: true },
    { label: "Sub-users", value: user.subUserCount, icon: FiUsers, color: "#7C3AED" },
  ];

  return (
    <section className="mid_wrap ust-page">
      <div className="container">
        {/* Back link */}
        <Link to="/admin" className="ust-back">
          <FiArrowLeft size={16} /> Back to Admin Dashboard
        </Link>

        {/* User Profile Header */}
        <div className="ust-profile-card">
          <div className="ust-profile-left">
            <div className="ust-avatar">
              {(user.first_name || "U").charAt(0).toUpperCase()}
            </div>
            <div className="ust-profile-info">
              <h1 className="ust-name">{user.first_name} {user.last_name}</h1>
              <p className="ust-email">{user.username}</p>
              <div className="ust-profile-tags">
                <span className="ust-tag-plan">{getPlanLabel(user.access)}</span>
                <span className={`ust-tag-status ${user.active === 1 ? "ust-tag-active" : "ust-tag-inactive"}`}>
                  {user.active === 1 ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>
          <div className="ust-profile-meta">
            <div className="ust-meta-item">
              <FiCalendar size={14} />
              <span>Joined {formatDate(user.createdAt)}</span>
            </div>
            <div className="ust-meta-item">
              <FiClock size={14} />
              <span>Last login {timeAgo(user.last_login)}</span>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="ust-stats-grid">
          {statCards.map(({ label, value, icon: Icon, color, raw }) => (
            <div className="ust-stat-card" key={label}>
              <div className="ust-stat-icon" style={{ backgroundColor: `${color}12`, color }}>
                <Icon size={18} />
              </div>
              <div className="ust-stat-data">
                <span className="ust-stat-number">{raw ? value : formatNumber(value)}</span>
                <span className="ust-stat-label">{label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Integrations & Configuration Status */}
        <div className="ust-card ust-keys-card">
          <h3 className="ust-card-title">
            <FiKey size={16} style={{ marginRight: 6 }} />
            Integrations & Configuration
          </h3>
          <div className="ust-keys-grid">
            {/* AI Keys */}
            {aiKeys && (
              <>
                <div className={`ust-key-item ${aiKeys.openai ? "ust-key-active" : "ust-key-missing"}`}>
                  <div className="ust-key-status-icon">
                    {aiKeys.openai ? <FiCheck size={14} /> : <FiX size={14} />}
                  </div>
                  <div className="ust-key-info">
                    <span className="ust-key-name">OpenAI API Key</span>
                    <span className="ust-key-label">{aiKeys.openai ? "Configured" : "Not configured"}</span>
                  </div>
                </div>
                <div className={`ust-key-item ${aiKeys.gemini ? "ust-key-active" : "ust-key-missing"}`}>
                  <div className="ust-key-status-icon">
                    {aiKeys.gemini ? <FiCheck size={14} /> : <FiX size={14} />}
                  </div>
                  <div className="ust-key-info">
                    <span className="ust-key-name">Gemini API Key</span>
                    <span className="ust-key-label">{aiKeys.gemini ? "Configured" : "Not configured"}</span>
                  </div>
                </div>
              </>
            )}

            {/* SMTP */}
            {smtp && (
              <div className={`ust-key-item ${smtp.configured ? "ust-key-active" : "ust-key-missing"}`}>
                <div className="ust-key-status-icon">
                  {smtp.configured ? <FiCheck size={14} /> : <FiX size={14} />}
                </div>
                <div className="ust-key-info">
                  <span className="ust-key-name">SMTP</span>
                  <span className="ust-key-label">
                    {smtp.configured
                      ? `${smtp.host}${smtp.senderEmail ? ` · ${smtp.senderEmail}` : ""}`
                      : "Not configured"}
                  </span>
                </div>
              </div>
            )}

            {/* Whitelabel */}
            {whitelabel && (
              <div className={`ust-key-item ${whitelabel.configured ? "ust-key-active" : "ust-key-missing"}`}>
                <div className="ust-key-status-icon">
                  {whitelabel.configured ? <FiCheck size={14} /> : <FiX size={14} />}
                </div>
                <div className="ust-key-info">
                  <span className="ust-key-name">Whitelabel</span>
                  <span className="ust-key-label">
                    {whitelabel.configured ? (whitelabel.siteTitle || "Configured") : "Not configured"}
                  </span>
                </div>
              </div>
            )}

            {/* Autoresponders */}
            {autoresponderStatus && ["convertkit", "mailchimp", "getresponse", "activecampaign"].map(key => {
              const connected = autoresponderStatus[key] === true;
              const labels = {
                convertkit: "ConvertKit",
                mailchimp: "Mailchimp",
                getresponse: "GetResponse",
                activecampaign: "ActiveCampaign"
              };
              return (
                <div key={key} className={`ust-key-item ${connected ? "ust-key-active" : "ust-key-missing"}`}>
                  <div className="ust-key-status-icon">
                    {connected ? <FiCheck size={14} /> : <FiX size={14} />}
                  </div>
                  <div className="ust-key-info">
                    <span className="ust-key-name">{labels[key]}</span>
                    <span className="ust-key-label">{connected ? "Connected" : "Not connected"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Projects Table */}
        <div className="ust-card">
          <h3 className="ust-card-title">Projects ({projects.length})</h3>
          {projects.length > 0 ? (
            <div className="ust-table-wrap">
              <table className="ust-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Niche</th>
                    <th>Status</th>
                    <th>Pages</th>
                    <th>Domain</th>
                    <th>Cost</th>
                    <th>App</th>
                    <th>Created</th>
                    <th>Published</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p, i) => {
                    const sc = STATUS_COLORS[p.status] || STATUS_COLORS.draft;
                    return (
                      <tr key={p.id}>
                        <td className="ust-td-index">{i + 1}</td>
                        <td className="ust-td-name">{p.name}</td>
                        <td><span className="ust-niche-tag">{p.niche || "—"}</span></td>
                        <td>
                          <span className="ust-status-badge" style={{ backgroundColor: sc.bg, color: sc.text }}>
                            {p.status}
                          </span>
                        </td>
                        <td>{p.no_of_pages || 0}</td>
                        <td className="ust-td-domain">
                          {p.custom_domain ? (
                            <a href={`https://${p.custom_domain}`} target="_blank" rel="noopener noreferrer" className="ust-domain-link">
                              {p.custom_domain}
                            </a>
                          ) : p.subdomain ? (
                            <a href={getSubdomainUrl(p.subdomain)} target="_blank" rel="noopener noreferrer" className="ust-domain-link">
                              {p.subdomain}
                            </a>
                          ) : "—"}
                        </td>
                        <td>
                          <span className={`ust-cost-badge ${p.cost > 0 ? "ust-cost-active" : ""}`}>
                            ${(p.cost || 0).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          {p.appBuiltPlatforms ? (
                            <div className="ust-app-badges">
                              {p.appBuiltPlatforms.split(",").map(platform => (
                                <span key={platform} className={`ust-app-badge ust-app-${platform.trim()}`}>
                                  {platform.trim() === "android" ? "Android" : "iOS"}
                                </span>
                              ))}
                            </div>
                          ) : "—"}
                        </td>
                        <td>{formatDate(p.createdAt)}</td>
                        <td>{p.published_at ? formatDate(p.published_at) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="ust-empty">No projects</p>
          )}
        </div>

        {/* Sub-users Table */}
        <div className="ust-card">
          <h3 className="ust-card-title">Sub-users ({subUsers.length})</h3>
          {subUsers.length > 0 ? (
            <div className="ust-table-wrap">
              <table className="ust-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Email</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Projects</th>
                    <th>Last Login</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {subUsers.map((su, i) => (
                    <tr key={su.id}>
                      <td className="ust-td-index">{i + 1}</td>
                      <td>
                        <span
                          className="ust-subuser-link"
                          onClick={() => history.push(`/admin/user/${su.id}`)}
                        >
                          {su.first_name} {su.last_name}
                        </span>
                      </td>
                      <td>{su.username}</td>
                      <td><span className="ust-plan-badge">{getPlanLabel(su.access)}</span></td>
                      <td>
                        <span className={`ust-status-pill ${su.active === 1 ? "ust-pill-active" : "ust-pill-inactive"}`}>
                          {su.active === 1 ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>{su.projectCount || 0}</td>
                      <td>{timeAgo(su.last_login)}</td>
                      <td>{formatDate(su.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="ust-empty">No sub-users</p>
          )}
        </div>

        {/* Recent Submissions */}
        <div className="ust-card">
          <h3 className="ust-card-title">Recent Submissions ({submissions.length})</h3>
          {submissions.length > 0 ? (
            <div className="ust-table-wrap">
              <table className="ust-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Project</th>
                    <th>Email</th>
                    <th>Page</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s, i) => (
                    <tr key={s.id}>
                      <td className="ust-td-index">{i + 1}</td>
                      <td>{s.project?.name || "—"}</td>
                      <td>{s.email || "—"}</td>
                      <td>{s.page_name || "—"}</td>
                      <td>
                        <span className={`ust-sub-status ust-sub-${s.status}`}>{s.status}</span>
                      </td>
                      <td>{timeAgo(s.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="ust-empty">No submissions</p>
          )}
        </div>

        {/* Recent Activity */}
        <div className="ust-card">
          <h3 className="ust-card-title">
            <FiActivity size={16} style={{ marginRight: 6 }} />
            Recent Activity
          </h3>
          {recentActivity && recentActivity.length > 0 ? (
            <div className="ust-activity-list">
              {recentActivity.map((item, i) => {
                const iconMap = {
                  project_created: { icon: FiPlus, color: "#8B5CF6", bg: "#F5F3FF" },
                  project_published: { icon: FiGlobe, color: "#10B981", bg: "#ECFDF5" },
                  app_built: { icon: FiSmartphone, color: "#3B82F6", bg: "#EFF6FF" },
                  form_submission: { icon: FiSend, color: "#F59E0B", bg: "#FFFBEB" },
                };
                const cfg = iconMap[item.type] || iconMap.project_created;
                const Icon = cfg.icon;
                return (
                  <div className="ust-activity-item" key={i}>
                    <div className="ust-activity-icon" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                      <Icon size={14} />
                    </div>
                    <div className="ust-activity-content">
                      <span className="ust-activity-title">{item.title}</span>
                      {item.detail && <span className="ust-activity-detail">{item.detail}</span>}
                    </div>
                    <span className="ust-activity-time">{timeAgo(item.date)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="ust-empty">No recent activity</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default UserStats;
